// frontend/src/components/ProblemCard.tsx
import React, { useState, useEffect } from 'react';
import { Problem, apiService, ProgrammingLanguage, AlgorithmWalkthrough } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { useUsage } from '../contexts/UsageContext';
import { PatternSection } from './PatternSection';
import { SyntaxHighlighter } from './SyntaxHighlighter';
import { ComplexityInfo } from './ComplexityInfo';
import { DifficultyBadge } from './DifficultyBadge';
import { Badge } from './Badge';
import { CompletionStatusButton } from './CompletionStatusButton';
import { ChatHistoryIndicator } from './ChatHistoryIndicator';
import { ChatModal } from './ChatModal';
import { ChatSidePanel } from './ChatSidePanel';
import { useAuthModal } from '../hooks/useAuthModal';
import { AuthRequiredModal } from './AuthRequiredModal';
import { LoginModal } from './LoginModal';
import { AnimationGenerationModal, AnimationGenerationModalState } from './AnimationGenerationModal';
import { WalkthroughGenerationModal, WalkthroughGenerationModalState } from './WalkthroughGenerationModal';
import { SimpleUpArrowIcon, SimpleDownArrowIcon, DoubleUpArrowIcon, DoubleDownArrowIcon, DocumentIcon, CheckCircleIcon, CodeIcon, TagsVisibleIcon, TagsHiddenIcon, LeetCodeIcon } from './CustomIcons';
import { useUserSession } from '../hooks/useUserSession';
import { useLocation } from 'react-router-dom';
import { FEATURES } from '../config/features';
import { savePendingAction } from '../utils/authRedirectState';
import { useFirstTimeTooltip } from '../hooks/useFirstTimeTooltip';
import { FirstTimeTooltip } from './FirstTimeTooltip';
import { useActiveProblem } from '../contexts/ActiveProblemContext';

interface ProblemCardProps {
  problem: Problem;
  index: number;
  onProblemUpdate?: (updatedProblem: Problem) => void;
  customHeaderAction?: React.ReactNode;
  showTooltips?: boolean; // Only show first-time tooltips when true (to avoid showing on all cards)
}

export const ProblemCard: React.FC<ProblemCardProps> = ({ problem, index, onProblemUpdate, customHeaderAction, showTooltips = false }) => {
  const { userProfile, isAuthenticated } = useAuth();
  const location = useLocation();
  useUserSession();
  const { openPaywall, checkFeatureAccess } = useUsage();
  const {
    showAuthRequiredModal,
    showLoginModal,
    requireAuth,
    handleOk,
    handleLoginSuccess,
    handleClose: handleAuthClose
  } = useAuthModal();

  // First-time tooltips for expansion icons
  const {
    shouldShowTooltip,
    handleTooltipDismiss,
    getTooltipContent,
    shouldShowContextualTooltip,
    handleContextualTooltipDismiss,
    getContextualTooltipContent,
  } = useFirstTimeTooltip();

  // Active problem context for floating chat button
  const activeProblemContext = useActiveProblem();

  // Set this problem as active when user interacts with it
  const handleProblemInteraction = () => {
    activeProblemContext?.setActiveProblem({
      id: problem.id,
      title: problem.title,
    });
  };

  // Initialize expanded sections from restored session only
  const restoredSections = location.state?.expandedSections;

  const [showDescription, setShowDescription] = React.useState(restoredSections?.description ?? false);
  const [showPattern, setShowPattern] = React.useState(restoredSections?.algorithm ?? false);
  const [showAlgorithm, setShowAlgorithm] = React.useState(restoredSections?.algorithm ?? false);
  const [showCode, setShowCode] = React.useState(restoredSections?.code ?? false);
  const [, setShowSolutionInput] = React.useState(restoredSections?.walkthrough ?? false);
  
  // Code generation state
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  
  // Copy functionality state
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);
  
  // Animation modal state
  const [animationModalState, setAnimationModalState] = useState<AnimationGenerationModalState>({
    phase: 'generating',
    approachName: '',
    problemTitle: problem.title
  });
  const [isAnimationModalOpen, setIsAnimationModalOpen] = useState(false);

  // Walkthrough state
  const [walkthroughsVisible, setWalkthroughsVisible] = useState<{[approachName: string]: boolean}>({});
  const [walkthroughData, setWalkthroughData] = useState<{[approachName: string]: AlgorithmWalkthrough[]}>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setLoadingWalkthroughs] = useState<{[approachName: string]: boolean}>({});
  
  // Walkthrough generation modal state
  const [isWalkthroughModalOpen, setIsWalkthroughModalOpen] = useState(false);
  const [walkthroughModalState, setWalkthroughModalState] = useState<WalkthroughGenerationModalState>({
    phase: 'generating',
    approachName: '',
    problemTitle: problem.title
  });

  // Local problem state to update UI immediately
  const [localProblem, setLocalProblem] = useState<Problem>(problem);
  
  // Completion status state
  const [isCompleted, setIsCompleted] = useState(false);

  // Tag visibility state
  const [showTags, setShowTags] = useState(true);

  // Chat modal state
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  const expandAll = () => {
    setShowDescription(true);
    setShowPattern(true);
    setShowAlgorithm(true);
    setShowCode(true);
    // Note: Solution Input section is controlled by PatternSection internally
  };

  const collapseAll = () => {
    setShowDescription(false);
    setShowPattern(false);
    setShowAlgorithm(false);
    setShowCode(false);
    // Note: Solution Input section is controlled by PatternSection internally
  };

  const isAllExpanded = showDescription && showPattern && showAlgorithm && showCode;

  // Don't save on every change - only save when app closes (handled in useUserSession)

  // Restore scroll position if this was a restored session
  useEffect(() => {
    if (location.state?.restored && location.state?.scrollPosition) {
      window.scrollTo(0, location.state.scrollPosition);
    }
  }, [location.state]);

  // Listen for auth redirect completion to resume pending actions
  useEffect(() => {
    const handleAuthRedirect = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { pendingAction } = customEvent.detail;

      // Only handle actions for this specific problem
      if (pendingAction?.data?.problemId !== problem.id) {
        return;
      }

      switch (pendingAction?.type) {
        case 'open_chat':
          // Resume opening chat modal for this specific problem
          openChatModal();
          break;
        case 'generate_animation':
          // Resume animation generation for this specific problem
          if (pendingAction.data.approachName) {
            handleAnimateClick(pendingAction.data.approachName);
          }
          break;
        case 'generate_walkthrough':
          // Resume walkthrough generation for this specific problem
          if (pendingAction.data.approachName) {
            handleWalkthroughClick(pendingAction.data.approachName);
          }
          break;
      }
    };

    window.addEventListener('auth-redirect-complete', handleAuthRedirect);
    return () => window.removeEventListener('auth-redirect-complete', handleAuthRedirect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problem.id]);

  const handleCompletionChange = (completed: boolean) => {
    setIsCompleted(completed);
  };

  const handleSolutionInputToggle = (isExpanded: boolean) => {
    setShowSolutionInput(isExpanded);
  };

  const handleChatHistoryClick = async () => {
    // If not authenticated, save action before redirect
    if (!isAuthenticated) {
      savePendingAction({
        type: 'open_chat',
        data: { problemId: problem.id }
      });
      // Trigger auth flow (action will resume after redirect)
      requireAuth(() => handleChatHistoryClick());
      return;
    }

    // Pre-check credits before opening chat to avoid user typing a message and losing it
    const hasAccess = await checkFeatureAccess('chatTutor');
    if (!hasAccess) {
      // Paywall modal is shown by checkFeatureAccess
      return;
    }

    // User is authenticated and has credits, open chat
    openChatModal();
  };

  const openChatModal = () => {
    setIsChatModalOpen(true);
  };

  const handleCloseChatModal = () => {
    setIsChatModalOpen(false);
  };

  const handleAnimateClick = async (approachName: string) => {
    console.log(`🎬 [Frontend] handleAnimateClick called for approach: "${approachName}"`);
    console.log(`🎬 [Frontend] Problem ID: ${localProblem.id}`);
    console.log(`🎬 [Frontend] Problem title: ${localProblem.title}`);

    // Check authentication first - prompt for sign-in if not authenticated
    if (!isAuthenticated) {
      console.log(`🔐 [Frontend] User not authenticated, prompting for sign-in`);
      savePendingAction({
        type: 'generate_animation',
        data: { problemId: localProblem.id, approachName }
      });
      requireAuth(() => handleAnimateClick(approachName));
      return;
    }

    // Initialize modal state and open modal
    setAnimationModalState({
      phase: 'generating',
      approachName,
      problemTitle: localProblem.title,
      timeElapsed: 0
    });
    setIsAnimationModalOpen(true);
    
    // Track time for progress
    const startTime = Date.now();
    const timeTracker = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setAnimationModalState(prev => ({
        ...prev,
        timeElapsed: elapsed
      }));
    }, 1000);
    
    try {
      console.log(`🎬 [Frontend] Starting animation generation for approach: ${approachName}`);
      console.log(`🎬 [Frontend] Calling apiService.generateAlgorithmAnimation...`);
      
      const signedUrl = await apiService.generateAlgorithmAnimation(localProblem.id, approachName);

      console.log(`✅ [Frontend] Animation URL generated successfully for ${approachName}`);
      console.log(`🔗 [Frontend] Signed URL: ${signedUrl}`);

      // Show success modal with "Open Animation" button
      clearInterval(timeTracker);
      setAnimationModalState({
        phase: 'success',
        approachName,
        problemTitle: localProblem.title,
        animationUrl: signedUrl,
        timeElapsed: Date.now() - startTime
      });
      // Modal stays open - user clicks "Open Animation" button to view

      console.log(`🎭 [Frontend] Animation generation completed for ${approachName}`);
    } catch (error) {
      console.error(`❌ [Frontend] Failed to generate animation for ${approachName}:`, error);
      console.error(`❌ [Frontend] Error details:`, {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined
      });

      clearInterval(timeTracker);

      // Check if this is a usage limit error (402 Payment Required)
      // The error is wrapped by api.ts with originalError containing the actual status
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorObj = error as any;
      const isUsageLimitError =
        errorObj?.code === 'usage-limit-exceeded' ||
        errorObj?.originalError?.status === 402 ||
        errorObj?.originalError?.data?.error?.includes?.('usage-limit-exceeded') ||
        errorObj?.status === 402 ||
        errorMessage.includes('usage-limit-exceeded');

      if (isUsageLimitError) {
        console.log(`💳 [Frontend] Usage limit exceeded, opening paywall`);
        setIsAnimationModalOpen(false);
        openPaywall('no-credits', 'animation-generation');
      } else {
        // Update modal to error state for other errors
        setAnimationModalState(prev => ({
          ...prev,
          phase: 'error',
          message: errorMessage,
          timeElapsed: Date.now() - startTime
        }));
      }
    }
  };

  const handleCloseAnimationModal = () => {
    setIsAnimationModalOpen(false);
  };

  const handleOpenAnimation = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    
    // Start checking for feedback in localStorage
    checkForAnimationFeedback();
  };

  // Walkthrough handlers
  const handleWalkthroughClick = async (approachName: string) => {
    console.log(`🚶 [Frontend] handleWalkthroughClick called for approach: "${approachName}"`);

    // Toggle visibility if already visible
    const isCurrentlyVisible = walkthroughsVisible[approachName];
    if (isCurrentlyVisible) {
      console.log(`🚶 [Frontend] Hiding walkthrough for ${approachName}`);
      setWalkthroughsVisible(prev => ({ ...prev, [approachName]: false }));
      return;
    }

    // Check if we already have walkthrough data cached locally
    if (walkthroughData[approachName] && walkthroughData[approachName].length > 0) {
      console.log(`🚶 [Frontend] Showing existing walkthrough data for ${approachName}`);
      setWalkthroughsVisible(prev => ({ ...prev, [approachName]: true }));
      // Scroll to walkthrough section
      setTimeout(() => scrollToWalkthrough(approachName), 100);
      return;
    }

    // Open modal IMMEDIATELY with "checking" phase for instant feedback
    console.log(`🚶 [Frontend] Opening modal with checking phase for ${approachName}`);
    setWalkthroughModalState({
      phase: 'checking',
      approachName: approachName,
      problemTitle: problem.title,
      timeElapsed: 0
    });
    setIsWalkthroughModalOpen(true);

    // Now check if walkthrough exists in database
    console.log(`🚶 [Frontend] Checking for existing walkthroughs for ${approachName}`);
    try {
      const existingWalkthroughs = await apiService.getAlgorithmWalkthroughs(localProblem.id, approachName);
      console.log(`🚶 [Frontend] Found ${existingWalkthroughs.length} existing walkthroughs for ${approachName}`);
      console.log(`🚶 [Frontend] Existing walkthroughs structure:`, JSON.stringify(existingWalkthroughs, null, 2));

      if (existingWalkthroughs.length > 0) {
        // Close modal and show existing walkthrough directly
        console.log(`🚶 [Frontend] Setting walkthrough data with existing walkthroughs`);
        setIsWalkthroughModalOpen(false);
        setWalkthroughData(prev => ({ ...prev, [approachName]: existingWalkthroughs }));
        setWalkthroughsVisible(prev => ({ ...prev, [approachName]: true }));
        setTimeout(() => scrollToWalkthrough(approachName), 100);
        return;
      }

      // No existing walkthrough - need to generate (requires auth)
      console.log(`🚶 [Frontend] No existing walkthroughs found for ${approachName}`);

      // Check authentication before generation - prompt for sign-in if not authenticated
      if (!isAuthenticated) {
        console.log(`🔐 [Frontend] User not authenticated, closing modal and prompting for sign-in`);
        setIsWalkthroughModalOpen(false);
        savePendingAction({
          type: 'generate_walkthrough',
          data: { problemId: localProblem.id, approachName }
        });
        requireAuth(() => handleWalkthroughClick(approachName));
        return;
      }

      // User is authenticated - switch to generation phase
      console.log(`🚶 [Frontend] Switching to generation phase for ${approachName}`);
      setWalkthroughModalState({
        phase: 'generating',
        approachName: approachName,
        problemTitle: problem.title,
        timeElapsed: 0
      });

      // Start generation timer
      const startTime = Date.now();
      const timer = setInterval(() => {
        setWalkthroughModalState(prev => ({
          ...prev,
          timeElapsed: Date.now() - startTime
        }));
      }, 1000);

      // Generate walkthrough
      const newWalkthrough = await apiService.generateAlgorithmWalkthrough(localProblem.id, approachName);
      console.log(`🚶 [Frontend] Generated new walkthrough for ${approachName}:`, newWalkthrough);
      console.log(`🚶 [Frontend] Type of newWalkthrough:`, typeof newWalkthrough);
      console.log(`🚶 [Frontend] Structure of newWalkthrough:`, JSON.stringify(newWalkthrough, null, 2));

      clearInterval(timer);

      // Update state with new walkthrough
      // Check if newWalkthrough has a data property (wrapped response) or is the walkthrough itself
      const walkthroughToAdd = (newWalkthrough as any).data || newWalkthrough;
      console.log(`🚶 [Frontend] walkthroughToAdd after extraction:`, walkthroughToAdd);
      console.log(`🚶 [Frontend] Type of walkthroughToAdd:`, typeof walkthroughToAdd);
      console.log(`🚶 [Frontend] Structure of walkthroughToAdd:`, JSON.stringify(walkthroughToAdd, null, 2));
      setWalkthroughData(prev => ({ ...prev, [approachName]: [walkthroughToAdd] }));
      setWalkthroughsVisible(prev => ({ ...prev, [approachName]: true }));

      // Show success modal briefly, then close and scroll
      setWalkthroughModalState(prev => ({
        ...prev,
        phase: 'success',
        timeElapsed: Date.now() - startTime
      }));

      setTimeout(() => {
        setIsWalkthroughModalOpen(false);
        setTimeout(() => scrollToWalkthrough(approachName), 100);
      }, 2000);

    } catch (error) {
      console.error(`❌ [Frontend] Failed to load/generate walkthrough for ${approachName}:`, error);

      // Check if this is a usage limit error (402 Payment Required)
      const errorObj = error as any;
      const isUsageLimitError =
        errorObj?.code === 'usage-limit-exceeded' ||
        errorObj?.originalError?.status === 402 ||
        errorObj?.originalError?.data?.error?.includes?.('usage-limit-exceeded') ||
        errorObj?.status === 402;

      if (isUsageLimitError) {
        console.log(`💳 [Frontend] Usage limit exceeded for walkthrough, opening paywall`);
        setIsWalkthroughModalOpen(false);
        openPaywall('no-credits', 'walkthrough-generation');
      } else {
        // Show generic error modal for other errors
        setWalkthroughModalState(prev => ({
          ...prev,
          phase: 'error',
          message: error instanceof Error ? error.message : 'Failed to generate walkthrough. Please try again.'
        }));
      }
    }
  };

  // Helper function to scroll to walkthrough section
  const scrollToWalkthrough = (approachName: string) => {
    const walkthroughElement = document.querySelector(`[data-walkthrough="${approachName}"]`);
    if (walkthroughElement) {
      walkthroughElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Handle walkthrough modal close
  const handleCloseWalkthroughModal = () => {
    setIsWalkthroughModalOpen(false);
  };

  const checkForAnimationFeedback = () => {
    // Check for feedback every 5 seconds for up to 2 minutes
    let attempts = 0;
    const maxAttempts = 24; // 2 minutes with 5-second intervals
    
    const feedbackChecker = setInterval(() => {
      attempts++;
      
      const feedbackData = localStorage.getItem('animation-feedback');
      if (feedbackData) {
        try {
          const feedback = JSON.parse(feedbackData);
          console.log('📊 Animation feedback received:', feedback);
          
          // TODO: Send to backend API for persistence
          // apiService.submitAnimationFeedback(feedback);
          
          // Clean up
          localStorage.removeItem('animation-feedback');
          clearInterval(feedbackChecker);
          
          // Show thank you message (optional)
          console.log(`✅ Thank you for rating the ${feedback.approachName} animation as ${feedback.feedback}`);
          
        } catch (error) {
          console.error('Error parsing animation feedback:', error);
          localStorage.removeItem('animation-feedback'); // Clean up invalid data
        }
      }
      
      // Stop checking after max attempts
      if (attempts >= maxAttempts) {
        clearInterval(feedbackChecker);
        console.log('⏰ Stopped checking for animation feedback after 2 minutes');
      }
    }, 5000); // Check every 5 seconds
  };


  const copyToClipboard = async (text: string, codeId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCodeId(codeId);
      setTimeout(() => setCopiedCodeId(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleCodeSectionClick = async () => {
    setShowCode(!showCode);
    
    // If opening code section and user has preferred language other than python
    if (!showCode && userProfile?.preferredLanguage && userProfile.preferredLanguage !== 'python') {
      const userLanguage = userProfile.preferredLanguage;
      
      // Check if implementations exist for user's preferred language
      if (!localProblem.code?.implementations?.[userLanguage]) {
        setIsGeneratingCode(true);
        
        try {
          console.log(`🔄 Generating ${userLanguage} code for problem: ${localProblem.id}`);
          const implementations = await apiService.generateLanguageCode(localProblem.id, userLanguage);
          
          // Update local problem with generated implementations
          const updatedProblem = {
            ...localProblem,
            code: {
              ...localProblem.code,
              implementations: {
                ...(localProblem.code?.implementations || {}),
                [userLanguage]: implementations
              }
            }
          };
          
          setLocalProblem(updatedProblem);
          
          // Notify parent if callback provided
          if (onProblemUpdate) {
            onProblemUpdate(updatedProblem);
          }
          
          console.log(`✅ Generated ${userLanguage} implementations for problem`);
        } catch (error) {
          console.error(`Failed to generate ${userLanguage} code:`, error);
          // Show error message but still allow code section to open
        } finally {
          setIsGeneratingCode(false);
        }
      }
    }
  };

  const getLanguageDisplayName = (lang: ProgrammingLanguage): string => {
    const displayNames: Record<ProgrammingLanguage, string> = {
      python: 'Python',
      typescript: 'TypeScript',
      java: 'Java',
      golang: 'Go'
    };
    return displayNames[lang];
  };

  const getCodeImplementations = () => {
    const userLanguage = userProfile?.preferredLanguage || 'python';
    const implementations = localProblem.code?.implementations?.[userLanguage];
    
    // If user's preferred language is available, use it
    if (implementations) {
      return { language: userLanguage, implementations };
    }
    
    // Fall back to Python if user's preferred language not available
    return { 
      language: 'python' as ProgrammingLanguage, 
      implementations: localProblem.code?.implementations?.python || [] 
    };
  };


  return (
    <div
      className="problem-card"
      onClick={handleProblemInteraction}
      onFocusCapture={handleProblemInteraction}
    >
      <div className="problem-header">
        <div className="problem-title">
          <span>
            {isCompleted && <span className="completion-checkmark">✓</span>}
            {index + 1}. {problem.title}
          </span>
          {problem.leetcode_number && (
            <span className="problem-id">(LeetCode {problem.leetcode_number})</span>
          )}
        </div>
        <div className="problem-header-right">
          <ChatHistoryIndicator
            problemId={problem.id}
            problemTitle={problem.title}
            onChatOpen={handleChatHistoryClick}
          />
          <CompletionStatusButton 
            problemId={problem.id} 
            onCompletionChange={handleCompletionChange}
          />
          <button
            onClick={() => setShowTags(!showTags)}
            className="header-btn tag"
            title={showTags ? "Hide tags" : "Show tags"}
            aria-label={showTags ? "Hide tags" : "Show tags"}
          >
            {showTags ? <TagsVisibleIcon size={16} color="white" /> : <TagsHiddenIcon size={16} color="white" />}
          </button>
          {customHeaderAction}
        </div>
      </div>

      {/* Tags and Difficulty */}
      {showTags && (
        <div className="flex flex-wrap gap-2 mb-4 items-center" style={{ marginLeft: '20px', marginRight: '20px' }}>
          <DifficultyBadge difficulty={problem.difficulty} />
          {problem.tags.slice(0, 5).map((tag, idx) => (
            <Badge key={idx} variant="secondary" size="small">
              {tag}
            </Badge>
          ))}
          {/* Company frequencies as tags */}
          {problem.companies && problem.companies.length > 0 && (
            problem.companies.slice(0, 3).map((company, idx) => {
              // Handle both string and object formats
              if (typeof company === 'string') {
                return (
                  <Badge key={`company-${company}`} variant="company" size="small">
                    {company}
                  </Badge>
                );
              }
              return (
                <Badge key={`company-${company.name}`} variant="company" size="small">
                  {company.name}:{company.frequency.toFixed(0)}%
                </Badge>
              );
            })
          )}
          {problem.tags.length > 5 && (
            <Badge variant="secondary" size="small">
              +{problem.tags.length - 5} more
            </Badge>
          )}
          {/* LeetCode link - positioned at the right edge */}
          {problem.leetcode_number && (
            <>
              <div style={{ flex: 1 }} /> {/* Spacer to push icon to right */}
              <a
                href={problem.leetcodeUrl || `https://leetcode.com/problems/${problem.title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="leetcode-link"
                title="View on LeetCode"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  opacity: 0.7
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0.7';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <LeetCodeIcon size={18} />
              </a>
            </>
          )}
        </div>
      )}

      {/* Removed GenerationStatusIndicator - placeholder problems have been cleaned up */}

      <PatternSection
        pattern={problem.pattern}
        problemId={problem.id}
        onSolutionInputToggle={handleSolutionInputToggle}
        showWorkbenchTooltip={showTooltips && shouldShowTooltip('practice-workbench')}
        workbenchTooltipMessage={getTooltipContent('practice-workbench')}
        onWorkbenchTooltipDismiss={() => handleTooltipDismiss('practice-workbench')}
      />

      {/* Horizontal Section Buttons */}
      <div className="sections">
        <div className="section-button-wrapper">
          <button
            className="section problem"
            onClick={() => {
              setShowDescription(!showDescription);
              handleTooltipDismiss('problem-description');
            }}
            type="button"
          >
            <div className="section-content">
              <DocumentIcon size={20} className="section-icon" />
              Problem Description
            </div>
            <span className="chevron-badge">
              {showDescription ? (
                <SimpleUpArrowIcon size={16} color="white" />
              ) : (
                <SimpleDownArrowIcon size={16} color="white" />
              )}
            </span>
          </button>
          <FirstTimeTooltip
            show={showTooltips && shouldShowTooltip('problem-description')}
            message={getTooltipContent('problem-description')}
            position="top"
          />
        </div>
        <div className="section-button-wrapper">
          <button
            className="section algorithm"
            onClick={() => {
              setShowAlgorithm(!showAlgorithm);
              handleTooltipDismiss('detailed-algorithm');
            }}
            type="button"
          >
            <div className="section-content">
              <CheckCircleIcon size={20} className="section-icon" />
              Detailed Algorithm
            </div>
            <span className="chevron-badge">
              {showAlgorithm ? (
                <SimpleUpArrowIcon size={16} color="white" />
              ) : (
                <SimpleDownArrowIcon size={16} color="white" />
              )}
            </span>
          </button>
          <FirstTimeTooltip
            show={showTooltips && shouldShowTooltip('detailed-algorithm')}
            message={getTooltipContent('detailed-algorithm')}
            position="top"
          />
        </div>
        <div className="section-button-wrapper">
          <button
            className="section code"
            onClick={() => {
              handleCodeSectionClick();
              handleTooltipDismiss('solution-code');
            }}
            type="button"
            disabled={isGeneratingCode}
          >
            <div className="section-content">
              <CodeIcon size={20} className="section-icon" />
              {isGeneratingCode ? 'Generating Code...' : 'Solution Code'}
            </div>
            {isGeneratingCode ? (
              <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
            ) : (
              <span className="chevron-badge">
                {showCode ? (
                  <SimpleUpArrowIcon size={16} color="white" />
                ) : (
                  <SimpleDownArrowIcon size={16} color="white" />
                )}
              </span>
            )}
          </button>
          <FirstTimeTooltip
            show={showTooltips && shouldShowTooltip('solution-code')}
            message={getTooltipContent('solution-code')}
            position="top"
          />
        </div>

        {/* Collapse/Expand All Icon */}
        <div className="section-button-wrapper expand-all-wrapper">
          <button
            className="btn-icon"
            onClick={() => {
              if (isAllExpanded) {
                collapseAll();
              } else {
                expandAll();
              }
              handleTooltipDismiss('expand-all');
            }}
            type="button"
            title={isAllExpanded ? "Collapse All Sections" : "Expand All Sections"}
          >
            {isAllExpanded ? (
              <DoubleUpArrowIcon size={21} color="white" />
            ) : (
              <DoubleDownArrowIcon size={21} color="white" />
            )}
          </button>
          <FirstTimeTooltip
            show={showTooltips && shouldShowTooltip('expand-all')}
            message={getTooltipContent('expand-all')}
            position="left"
          />
        </div>
      </div>

      {/* Problem Description Section */}
      {showDescription && (
        <div className="collapsible-section show problem-description">
          <div className="section-header">
            📋 Problem Description
          </div>
          <div className="section-content">
            {localProblem.description?.problem ? (
              <div dangerouslySetInnerHTML={{ __html: localProblem.description.problem }} />
            ) : (
              <p className="text-gray-500">No description available</p>
            )}
            
            {localProblem.description?.examples?.length > 0 && (
              <>
                <p><strong>Examples:</strong></p>
                <div className="space-y-3">
                  {localProblem.description.examples.map((example, idx) => (
                    <div key={idx} className="bg-gray-50 p-3 rounded-lg border-l-4 border-blue-500">
                      <div className="mb-2">
                        <strong>Input:</strong> <code className="bg-gray-200 px-2 py-1 rounded text-sm">{example.input}</code>
                      </div>
                      <div className="mb-2">
                        <strong>Output:</strong> <code className="bg-gray-200 px-2 py-1 rounded text-sm">{example.output}</code>
                      </div>
                      {example.explanation && (
                        <div className="text-gray-600">
                          <strong>Explanation:</strong> {example.explanation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {localProblem.description?.constraints?.length > 0 && (
              <>
                <p className="mt-4"><strong>Constraints:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  {localProblem.description.constraints.map((constraint, idx) => (
                    <li key={idx} className="text-sm">{constraint}</li>
                  ))}
                </ul>
              </>
            )}
            
            {localProblem.description?.followUp && (
              <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                <p><strong>Follow-up:</strong> {localProblem.description.followUp}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pattern Details Section */}
      {showPattern && (
        <div className="collapsible-section show pattern-details-section">
          <div className="section-header">
            🎯 Solution Pattern
          </div>
          <div className="section-content">
            <div className="pattern-text">
              <strong>{localProblem.pattern.name}</strong> - {localProblem.pattern.description}
            </div>
            <ul className="key-points">
              {localProblem.pattern.keyPoints.map((point, index) => (
                <li key={index} dangerouslySetInnerHTML={{ __html: point }} />
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Algorithm Details Section */}
      {showAlgorithm && (
        <div className="collapsible-section show algorithm-details">
          <div className="section-header">
            🧠 Detailed Algorithm Analysis
            <button
              onClick={() => {
                const allAlgorithmsText = (localProblem.algorithm?.approaches || []).map((approach, idx) => {
                  const algorithmText = `${approach.name}

Steps:
${approach.steps.map((step, i) => `${i + 1}. ${step.replace(/<[^>]*>/g, '')}`).join('\n')}

${approach.insights && approach.insights.length > 0 ? `Key Insights:
${approach.insights.map(insight => `• ${insight.replace(/<[^>]*>/g, '')}`).join('\n')}

` : ''}Time Complexity: ${approach.complexity.time}
Space Complexity: ${approach.complexity.space}${approach.complexity.notes ? `
Notes: ${approach.complexity.notes}` : ''}`;
                  return algorithmText;
                }).join('\n\n' + '='.repeat(50) + '\n\n');
                
                copyToClipboard(allAlgorithmsText, `${localProblem.id}-all-algorithms`);
              }}
              className="text-gray-600 hover:text-blue-600 p-1 rounded hover:bg-gray-100 transition-colors"
              title="Copy all algorithm approaches"
            >
              {copiedCodeId === `${localProblem.id}-all-algorithms` ? (
                <span className="text-green-600">✓</span>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
          <div className="section-content">
            {localProblem.algorithm?.approaches ? localProblem.algorithm.approaches.map((approach, idx) => (
              <div key={idx} className="mb-6">
                <div className="flex items-center justify-between mb-3 w-full">
                  <h4 className="text-lg font-semibold text-green-700 flex-shrink-0">🔥 {approach.name}</h4>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                    <span style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={() => {
                          handleAnimateClick(approach.name);
                          handleContextualTooltipDismiss('animation-button');
                        }}
                        disabled={isAnimationModalOpen && animationModalState.approachName === approach.name && animationModalState.phase === 'generating'}
                        className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={`Generate animation for ${approach.name}`}
                      >
                        🎬 Animation
                      </button>
                      {/* Show tooltip only on first approach of first problem card */}
                      {showTooltips && idx === 0 && (
                        <FirstTimeTooltip
                          show={shouldShowContextualTooltip('animation-button')}
                          message={getContextualTooltipContent('animation-button')}
                          position="right"
                        />
                      )}
                    </span>
                    <button
                      onClick={() => handleWalkthroughClick(approach.name)}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      title={`${walkthroughsVisible[approach.name] ? 'Hide' : 'Show'} walkthrough for ${approach.name}`}
                    >
                      📝 Walkthrough
                    </button>
                  </div>
                </div>
                
                <div className="mb-4">
                  <h5 className="font-semibold mb-2">Steps:</h5>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    {approach.steps.map((step, stepIdx) => (
                      <li key={stepIdx} className="text-sm leading-relaxed">
                        <span dangerouslySetInnerHTML={{ __html: step }} />
                      </li>
                    ))}
                  </ol>
                </div>
                
                {approach.insights && approach.insights.length > 0 && (
                  <div className="mb-4">
                    <h5 className="font-semibold mb-2">💡 Key Insights:</h5>
                    <ul className="list-disc list-inside space-y-1 text-gray-700">
                      {approach.insights.map((insight, insightIdx) => (
                        <li key={insightIdx} className="text-sm">
                          <span dangerouslySetInnerHTML={{ __html: insight }} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <ComplexityInfo complexity={approach.complexity} />
                
                {/* Walkthrough Display */}
                {walkthroughsVisible[approach.name] && walkthroughData[approach.name] && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border" data-walkthrough={approach.name}>
                    <h5 className="font-semibold mb-3 flex items-center justify-between">
                      <span>📝 Algorithm Walkthrough</span>
                      <button
                        onClick={() => setWalkthroughsVisible(prev => ({ ...prev, [approach.name]: false }))}
                        className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-200 rounded ml-4"
                        title="Hide walkthrough"
                      >
                        ✕
                      </button>
                    </h5>
                    {walkthroughData[approach.name].map((walkthrough, wtIdx) => (
                      <div key={walkthrough?.id || wtIdx} className="mb-4">
                        <div className="mb-3">
                          <h6 className="font-medium text-sm text-gray-800 mb-1">{walkthrough?.title || 'Algorithm Walkthrough'}</h6>
                          <p className="text-xs text-gray-600 mb-2">{walkthrough?.description || ''}</p>
                          <div className="flex gap-4 text-xs text-gray-700 mb-3">
                            <span><strong>Input:</strong> {walkthrough?.inputData || 'N/A'}</span>
                            <span><strong>Expected Output:</strong> {walkthrough?.expectedOutput || 'N/A'}</span>
                          </div>
                        </div>
                        
                        {/* Step-by-step table */}
                        {walkthrough?.steps && Array.isArray(walkthrough.steps) && walkthrough.steps.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 px-2 py-1 text-left whitespace-nowrap" style={{width: '50px'}}>Step</th>
                                <th className="border border-gray-300 px-2 py-1 text-left whitespace-nowrap" style={{width: '60px'}}>Value</th>
                                <th className="border border-gray-300 px-2 py-1 text-left" style={{width: '15%'}}>Operation</th>
                                <th className="border border-gray-300 px-2 py-1 text-left" style={{width: '20%'}}>State</th>
                                {walkthrough.steps.some(step => step?.subarray) && (
                                  <th className="border border-gray-300 px-2 py-1 text-left" style={{width: '15%'}}>Subarray</th>
                                )}
                                <th className="border border-gray-300 px-2 py-1 text-left">Explanation</th>
                              </tr>
                            </thead>
                            <tbody>
                              {walkthrough.steps.map((step, stepIdx) => (
                                <tr key={stepIdx} className={stepIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="border border-gray-300 px-2 py-1 font-mono text-center">{step.index}</td>
                                  <td className="border border-gray-300 px-2 py-1 font-mono text-center">
                                    {typeof step.value === 'object' && step.value !== null
                                      ? JSON.stringify(step.value)
                                      : step.value}
                                  </td>
                                  <td className="border border-gray-300 px-2 py-1">{step.operation}</td>
                                  <td className="border border-gray-300 px-2 py-1 font-mono text-xs">
                                    {typeof step.state === 'object' && step.state !== null
                                      ? Object.entries(step.state).map(([key, value]) => {
                                          // Properly serialize arrays and objects
                                          const serializedValue = typeof value === 'object' && value !== null
                                            ? JSON.stringify(value)
                                            : String(value);
                                          return `${key}: ${serializedValue}`;
                                        }).join(', ')
                                      : step.state}
                                  </td>
                                  {walkthrough.steps.some(step => step.subarray) && (
                                    <td className="border border-gray-300 px-2 py-1 font-mono">{step.subarray || '-'}</td>
                                  )}
                                  <td className="border border-gray-300 px-2 py-1 text-xs">{step.explanation}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        ) : (
                          <div className="text-xs text-gray-500 italic">No walkthrough steps available</div>
                        )}

                        {/* Key Observations */}
                        {walkthrough.keyObservations && Array.isArray(walkthrough.keyObservations) && walkthrough.keyObservations.length > 0 && (
                          <div className="mt-3">
                            <h6 className="font-medium text-sm text-gray-800 mb-2">🔍 Key Observations:</h6>
                            <ul className="list-disc list-inside text-xs text-gray-700 space-y-1">
                              {walkthrough.keyObservations.map((observation, obsIdx) => (
                                <li key={obsIdx}>{typeof observation === 'string' ? observation : JSON.stringify(observation)}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Complexity Info */}
                        {walkthrough.complexity && (
                          <div className="mt-3 flex gap-4 text-xs text-gray-600">
                            <span><strong>Time:</strong> {walkthrough.complexity.time || 'N/A'}</span>
                            <span><strong>Space:</strong> {walkthrough.complexity.space || 'N/A'}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )) : (
              <p className="text-gray-500">No algorithm approaches available</p>
            )}
          </div>
        </div>
      )}

      {/* Code Section */}
      {showCode && (() => {
        const { language, implementations } = getCodeImplementations();
        const displayName = getLanguageDisplayName(language);
        
        return (
          <div className="collapsible-section show code-section">
            <div className="section-header">
              💻 {displayName} Implementation
              {userProfile?.preferredLanguage && userProfile.preferredLanguage !== 'python' && language === 'python' && (
                <span className="language-fallback-note">
                  ({userProfile.preferredLanguage} not available, showing Python)
                </span>
              )}
            </div>
            
            {isGeneratingCode ? (
              <div className="code-generation-loading">
                <div className="spinner"></div>
                <span>Generating {userProfile?.preferredLanguage} implementation...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {implementations.length === 0 ? (
                  <div className="no-implementations-message">
                    <p>No {displayName} implementations available for this problem.</p>
                  </div>
                ) : (
                  implementations.map((impl, idx) => {
                    const codeId = `${localProblem.id}-${language}-${idx}`;
                    
                    return (
                      <div key={idx}>
                        {impl.name && (
                          <div className="flex justify-between items-center bg-gray-700 text-gray-300 px-3 py-2 text-xs rounded-t-lg">
                            <span className="code-header">{impl.name}</span>
                            <button
                              onClick={() => copyToClipboard(impl.code, codeId)}
                              className="text-white hover:text-blue-400 p-2 rounded hover:bg-gray-600 transition-colors border border-gray-500 bg-blue-600"
                              title="Copy code"
                              style={{ minWidth: '40px', minHeight: '32px' }}
                            >
                              {copiedCodeId === codeId ? (
                                <span className="text-green-400">✓</span>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                          </div>
                        )}
                        <div className={`bg-gray-800 p-4 overflow-x-auto ${impl.name ? 'rounded-b-lg' : 'rounded-lg'}`}>
                          {!impl.name && (
                            <div className="flex justify-end mb-2">
                              <button
                                onClick={() => copyToClipboard(impl.code, codeId)}
                                className="text-white hover:text-blue-400 p-2 rounded hover:bg-gray-600 transition-colors border border-gray-500 bg-blue-600"
                                title="Copy code"
                                style={{ minWidth: '40px', minHeight: '32px' }}
                              >
                                {copiedCodeId === codeId ? (
                                  <span className="text-green-400">✓</span>
                                ) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          )}
                          <SyntaxHighlighter 
                            code={impl.code} 
                            language={language === 'golang' ? 'go' : language}
                          />
                        </div>
                        {impl.explanation && (
                          <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                            <p className="text-sm text-gray-700">{impl.explanation}</p>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Problem Metadata */}
      <div className="mt-4 pt-4 border-t border-gray-200" style={{ display: 'none' }}>
        <div className="flex justify-between items-center text-xs text-gray-500">
          <div>
            Created: {new Date(problem.createdAt).toLocaleDateString()}
          </div>
          <div>
            ID: {problem.id}
          </div>
        </div>
      </div>

      {/* Chat Interface - conditionally render Side Panel or Modal based on feature flag */}
      {FEATURES.USE_SIDE_PANEL_CHAT ? (
        <ChatSidePanel
          isOpen={isChatModalOpen}
          onClose={handleCloseChatModal}
          problemId={problem.id}
          problemTitle={problem.title}
        />
      ) : (
        <ChatModal
          isOpen={isChatModalOpen}
          onClose={handleCloseChatModal}
          problemId={problem.id}
          problemTitle={problem.title}
        />
      )}

      {/* Animation Generation Modal */}
      <AnimationGenerationModal
        isOpen={isAnimationModalOpen}
        onClose={handleCloseAnimationModal}
        state={animationModalState}
        onOpenAnimation={handleOpenAnimation}
      />

      {/* Walkthrough Generation Modal */}
      <WalkthroughGenerationModal
        isOpen={isWalkthroughModalOpen}
        onClose={handleCloseWalkthroughModal}
        state={walkthroughModalState}
      />

      {/* Auth Modals for Chat */}
      <AuthRequiredModal
        isOpen={showAuthRequiredModal}
        onClose={handleAuthClose}
        onOk={handleOk}
      />

      <LoginModal
        isOpen={showLoginModal}
        onClose={handleAuthClose}
        onSuccess={handleLoginSuccess}
      />

    </div>
  );
};

